import { Badge, Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { Clear, Close, FilterAlt, Restore, Search, Store } from "@mui/icons-material";
import useFilters from "../../../hooks/useFilters";
import ActionButton, { FilterButtonWithoutEffect, LightGreyButton } from "../Buttons/ActionButton";
import MultiSelect from "../Selectors/MultiSelect";
import SingleSelect from "../Selectors/SingleSelect";
import DisabledButton from "../Buttons/DisabledButton";
import CloseButton from "../Buttons/CloseButton";
import React from "react";
import Confirmation from "../Confirmation";
import { HtmlTooltip, StyledDarkRedInformationTypography } from "../StyledComponents/styled";
import { DetailedSelectionIconWithTooltip } from "../../../api_calls/utils";
import StoreDetailTable from "../DetailedStore/StoreSelectionModal";
import { useDispatch, useSelector } from "react-redux";
import { deleteItems } from "../../../Store/actions";

const FilterPanel = ({ filtersConfig, applyFilterFunc, clearFilterFunc, initiallyAppliedVals, onFilterClick, draftKeyForStore }) => {
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [showSelectionModal, setShowSelectionModal] = React.useState(false);
    const storeDetails = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    // Initialize the hook
    const {
        draftFilters,
        appliedFilters,
        appliedCount,
        summaryList,
        hasUnsavedChanges,
        open,
        openDrawer,
        closeDrawer,
        setDraftFilter,
        clearDraftFilter,
        resetDraftFilters,
        applyFilters,
        clearAppliedFilters,
        clearUnsavedFilter,
        reduxKeyForDetailedSelection
    } = useFilters({
        config: filtersConfig,
        initialApplied: initiallyAppliedVals,
        onApply: (filters) => {
            applyFilterFunc(filters);
        },
        onClear: () => {
            // clear filters and reload data
            clearFilterFunc();
        },
    });

    return (
        <>
            <Stack direction={"row"} spacing={2}>
                {/* --------- apply filter button ------- */}
                <FilterButtonWithoutEffect
                    onClick={() => {
                        Promise.resolve(onFilterClick()).then(() => {

                            openDrawer();
                        });
                        // openDrawer();
                    }}
                    label={
                        <>
                            Filters {appliedCount > 0 ? `(${appliedCount})` : ''}
                        </>
                    }
                    icon={<FilterAlt />} />
                {/* ----- clear filter button ------ */}
                {
                    appliedCount > 0 &&
                    <LightGreyButton
                        onClick={clearAppliedFilters}
                        label={"Clear All"}
                        icon={<Clear />}
                    />
                }

                {summaryList.length > 0 && (
                    <StyledDarkRedInformationTypography variant="body2" onClick={openDrawer} sx={{ cursor: 'pointer', fontWeight: "normal" }}>
                        Filtered by: {summaryList.slice(0, 4).join(', ')}
                        {summaryList.length > 4 && `, and + ${summaryList.length - 4} more`}
                    </StyledDarkRedInformationTypography>
                )}
            </Stack>
            <Box height={10} />

            {/* Filter drawer */}
            {
                filtersConfig.length !== 0 && open && (
                    <Drawer
                        anchor="right"
                        open={open}
                        onClose={(event, reason) => {
                            if (reason === "backdropClick") return;
                            closeDrawer();
                        }}
                        sx={{ zIndex: 1300 }}
                    >
                        <Box sx={{ width: 420, p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Typography variant="h6">Filters</Typography>
                            {/* Render each filter */}
                            {filtersConfig.map(({ key, label, type, selectionParam, options, enabled = true, disableSearch, showDetailedStoreSelection = false }) => {
                                if (!enabled) return null;
                                const opts = options();

                                const value = draftFilters[key];
                                // Derive defaultVal for MultiSelect/SingleSelect.
                                let defaultVal;
                                if (type === 'multi') {
                                    // Multi-select: match each saved value to its option object.
                                    defaultVal = Array.isArray(value)
                                        ? opts.filter(opt => value.some(v => {
                                            if (typeof selectionParam !== "undefined") {
                                                return v === opt[selectionParam];
                                            } else {
                                                return v === opt.id || v === opt.label || v === opt.value
                                            }
                                        }))
                                        : [];
                                } else {
                                    // Single-select: find the option object matching the saved value.
                                    defaultVal = value
                                        ? opts.find(opt => {
                                            if (typeof selectionParam !== "undefined") {
                                                return value === opt[selectionParam];
                                            } else {
                                                return value === opt.id || value === opt.label || value === opt.value
                                            }
                                        }) || null
                                        : null;
                                }

                                return (
                                    <Box key={key} sx={{ mt: 2 }}>
                                        {type === 'multi' ? (
                                            <MultiSelect
                                                label={label}
                                                enableFullWidth={true}
                                                selectionCheck={(option, value) => option.label === value.label}
                                                options={opts}
                                                defaultVal={defaultVal}
                                                usedFor="filter"
                                                disableSearch={disableSearch}
                                                onChangeCall={(selected) => {
                                                    // Convert the array of option objects into an array of primitive values
                                                    const newValues = selected.map(item =>
                                                        typeof selectionParam !== "undefined" ? item[selectionParam] : typeof item.id !== "undefined"
                                                            ? item.id
                                                            : typeof item.value !== "undefined"
                                                                ? item.value
                                                                : item.label
                                                    );
                                                    const prev = draftFilters[key] || [];
                                                    // Only update the filter when it actually changes
                                                    if (
                                                        !Array.isArray(prev) ||
                                                        newValues.length !== prev.length ||
                                                        newValues.some((v, i) => v !== prev[i])
                                                    ) {
                                                        setDraftFilter(key, newValues);
                                                    }
                                                }}
                                                additionalIconWithTooltip={showDetailedStoreSelection}
                                                additionalIconMarkup={(setOpen) => {
                                                    return DetailedSelectionIconWithTooltip((e) => {
                                                        e.stopPropagation();
                                                        setOpen(false);
                                                        setShowSelectionModal(true);
                                                    })
                                                }
                                                }
                                            // Add an X icon to call clearDraftFilter(key) when value.length > 0
                                            />
                                        ) : (
                                            <SingleSelect
                                                label={label}
                                                enableFullWidth={true}
                                                options={opts}
                                                allowClearance={true}
                                                selectionCheck={(option, val) => val && (option.label === val.label)}
                                                defaultVal={defaultVal}
                                                usedFor="filter"
                                                disableSearch={disableSearch}
                                                onChangeCall={(selected) => {
                                                    if (selected) {
                                                        // Convert the selected option to a primitive value
                                                        const newVal =
                                                            typeof selected.id !== "undefined"
                                                                ? selected.id
                                                                : typeof selectionParam !== "undefined" ? selected[selectionParam] : typeof selected.value !== "undefined"
                                                                    ? selected.value
                                                                    : selected.label;

                                                        if (draftFilters[key] !== newVal) {
                                                            setDraftFilter(key, newVal);
                                                        }
                                                    } else if (draftFilters[key] != null) {
                                                        // Only clear if there was a value
                                                        clearDraftFilter(key);
                                                    }
                                                }}
                                            // Add an X icon to call clearDraftFilter(key) when value is not null
                                            />
                                        )}
                                    </Box>
                                );
                            })}
                            {/* Sticky footer */}
                            <Box sx={{ mt: 'auto' }}>
                                {hasUnsavedChanges && (
                                    <StyledDarkRedInformationTypography variant="body2" sx={{ mb: 2, fontWeight: "normal" }}>
                                        Unsaved filter changes.
                                    </StyledDarkRedInformationTypography>
                                )}
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-end"
                                    width="100%"
                                    spacing={1}
                                >

                                    <CloseButton
                                        onClick={(e) => {
                                            // show confirmation when there are unsaved changes
                                            if (hasUnsavedChanges) {
                                                setShowConfirmation(true);
                                            } else {
                                                closeDrawer();
                                            }
                                        }}
                                        label={"Close"}
                                        icon={<Close />}
                                    />

                                    {/* ------------- reset button -------------- */}
                                    <Stack direction="row" spacing={1} alignItems="flex-end">
                                        {
                                            !hasUnsavedChanges ?
                                                <DisabledButton
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                    }}
                                                    label={"Reset selections"}
                                                    icon={<Restore />}
                                                /> :
                                                <ActionButton
                                                    onClick={resetDraftFilters}
                                                    label={"Reset selections"}
                                                    icon={<Restore />}
                                                />
                                        }
                                        {/* ----------- apply filters button -------- */}
                                        {
                                            !hasUnsavedChanges ?
                                                <ActionButton
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                    }}
                                                    label={"Apply"}
                                                    icon={<FilterAlt />}
                                                /> :
                                                <ActionButton
                                                    onClick={applyFilters}
                                                    label={"Apply"}
                                                    icon={<FilterAlt />}
                                                />
                                        }

                                    </Stack>

                                </Stack>
                            </Box>
                        </Box>
                        {
                            showConfirmation &&
                            <Confirmation
                                confirmationActionCall={() => {
                                    clearUnsavedFilter();
                                    closeDrawer();
                                }}
                                showConfirmationPopup={showConfirmation}
                                confirmationTitle={"Discard Changes?"}
                                confirmationMessage={<>
                                    <Typography variant="body2">
                                        You have unsaved filter changes. These will be discarded.
                                    </Typography>
                                    <Box height={10} />
                                    <Typography variant="body2">
                                        Do you want to proceed?
                                    </Typography>
                                </>}
                                closePopup={setShowConfirmation}
                            />
                        }

                        {
                            showSelectionModal && (
                                <StoreDetailTable
                                    open={showSelectionModal}
                                    onClose={() => {
                                        setShowSelectionModal(false);
                                        dispatchAction(deleteItems(["store_selection_pagination", "store_list_filter_applied", "detailed_stores_filtered"]));
                                    }}
                                    applyChangesOnSelect={false}
                                    selectedValues={draftFilters[draftKeyForStore] || []}
                                    toHandleApplyChanges={(selectedStoreIds, selectedStoreNames) => {
                                        // when user selects stores from the detailed selection modal, we want to update the filter drawer's draft filters but not apply the filters yet until user clicks on apply button
                                        setDraftFilter(draftKeyForStore, selectedStoreIds);
                                    }}
                                    storeOptionsList={storeDetails.store_list_response_for_filters}
                                />
                            )
                        }
                    </Drawer>
                )
            }
        </>
    );
};

export default FilterPanel;
